import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Loader2, Search, ShieldCheck, ArrowLeft, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = [
  { slug: '', label: 'All' },
  { slug: 'entertainment', label: 'DJs & Entertainment' },
  { slug: 'food', label: 'Catering' },
  { slug: 'drinks', label: 'Bar & Drinks' },
  { slug: 'photography', label: 'Photography' },
  { slug: 'staffing', label: 'Staffing' },
  { slug: 'decor', label: 'Decor' },
  { slug: 'logistics', label: 'Logistics & Rentals' },
];
const PRICE_LABEL = { per_hour: '/hr', per_person: '/person', per_hour_per_person: '/hr/person', flat: ' flat' };
const MARKUP = 0.18, PROCESSING = 0.03;
const needsGuests = (m) => m === 'per_person' || m === 'per_hour_per_person';
const needsHours = (m) => m === 'per_hour' || m === 'per_hour_per_person';
const custPrice = (p) => (p * (1 + MARKUP)).toFixed(0);

export function SupplierHireDialog({ open, onClose, city, onRequest, requesting }) {
  const [services, setServices] = useState(null);
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [choices, setChoices] = useState({});
  const [guests, setGuests] = useState('50');
  const [hours, setHours] = useState('4');
  const [notes, setNotes] = useState('');
  const [showAll, setShowAll] = useState(false);
  const cityLabel = (city || 'Denver').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  useEffect(() => {
    if (!open) { setSelected(null); setQ(''); setCat(''); setChoices({}); setNotes(''); return; }
    setServices(null);
    axios.get(`${API}/services/products`, { params: { city: (city || 'denver').toLowerCase(), active_only: true } })
      .then(r => setServices(r.data?.products || r.data || []))
      .catch(() => { setServices([]); toast.error('Could not load suppliers'); });
  }, [open, city]);

  const filtered = useMemo(() => {
    let l = Array.isArray(services) ? services : [];
    if (cat) l = l.filter(s => s.category === cat);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      l = l.filter(s => `${s.name} ${s.host?.business_name || ''} ${s.sub_category || ''}`.toLowerCase().includes(needle));
    }
    return l;
  }, [services, cat, q]);
  const list = showAll ? filtered : filtered.slice(0, 30);

  const estimate = useMemo(() => {
    if (!selected) return null;
    let unit = Number(selected.base_price) || 0;
    (selected.option_groups || []).forEach(og => {
      const picked = (og.choices || []).find(c => c.choice_id === choices[og.option_id]);
      if (picked) unit += Number(picked.price_modifier) || 0;
    });
    const m = selected.pricing_model;
    const units = (needsGuests(m) ? Math.max(1, parseInt(guests) || 1) : 1) * (needsHours(m) ? Math.max(1, parseFloat(hours) || 1) : 1);
    const wholesale = unit * units;
    const total = wholesale * (1 + MARKUP) * (1 + PROCESSING);
    return { wholesale, total, deposit: total / 2, units };
  }, [selected, choices, guests, hours]);

  const pick = (s) => {
    setSelected(s);
    const defaults = {};
    (s.option_groups || []).forEach(og => {
      if (og.required && og.choices?.length) defaults[og.option_id] = og.choices[0].choice_id;
    });
    setChoices(defaults);
  };

  const submit = () => {
    const missing = (selected.option_groups || []).find(og => og.required && !choices[og.option_id]);
    if (missing) { toast.error(`Pick an option for "${missing.name}"`); return; }
    onRequest({
      service_id: selected.service_id,
      selected_choices: Object.values(choices).filter(Boolean),
      guest_count: needsGuests(selected.pricing_model) ? parseInt(guests) || 0 : 0,
      hours: needsHours(selected.pricing_model) ? parseFloat(hours) || 0 : 0,
      notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="supplier-hire-dialog">
        <DialogHeader>
          <DialogTitle>{selected ? 'Request to book' : 'Find & Hire Local Vendors'}</DialogTitle>
          <DialogDescription>
            {selected
              ? 'The vendor gets notified instantly. When they accept, you pay a 50% deposit to lock the date — Eventism handles the contract, payment and payout.'
              : `Vetted suppliers serving ${cityLabel} — customize a package and request to book.`}
          </DialogDescription>
        </DialogHeader>

        {selected ? (
          <div className="space-y-4" data-testid="hire-confirm">
            <button onClick={() => setSelected(null)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="h-3 w-3" /> Back to suppliers
            </button>
            <div className="flex gap-4">
              {selected.image_url && <img src={selected.image_url} alt="" className="h-24 w-32 object-cover rounded-lg border border-border" />}
              <div className="min-w-0">
                <p className="font-bold">{selected.name}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  {selected.host?.business_name || 'Verified supplier'}
                  {selected.host?.is_verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />}
                </p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{selected.short_description || selected.description}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Lock className="h-3 w-3" /> Contact details unlock when the vendor accepts</p>
              </div>
            </div>

            {(selected.option_groups || []).map(og => (
              <div key={og.option_id}>
                <p className="text-xs font-semibold mb-1.5">{og.name}{og.required && <span className="text-rose-400"> *</span>}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(og.choices || []).map(ch => (
                    <button key={ch.choice_id}
                      onClick={() => setChoices(prev => ({ ...prev, [og.option_id]: prev[og.option_id] === ch.choice_id && !og.required ? null : ch.choice_id }))}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${choices[og.option_id] === ch.choice_id
                        ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-border hover:border-fuchsia-500/60'}`}
                      data-testid={`opt-${ch.choice_id}`}>
                      {ch.name}{Number(ch.price_modifier) > 0 && ` +$${ch.price_modifier}`}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-2">
              {needsGuests(selected.pricing_model) && (
                <div><label className="text-xs text-muted-foreground">Guest count</label>
                  <Input type="number" min="1" value={guests} onChange={e => setGuests(e.target.value)} data-testid="booking-guests" /></div>
              )}
              {needsHours(selected.pricing_model) && (
                <div><label className="text-xs text-muted-foreground">Hours</label>
                  <Input type="number" min="1" step="0.5" value={hours} onChange={e => setHours(e.target.value)} data-testid="booking-hours" /></div>
              )}
            </div>
            <Textarea placeholder="Anything the vendor should know (set times, power, load-in…)" rows={2}
              value={notes} onChange={e => setNotes(e.target.value)} data-testid="booking-notes" />

            {estimate && (
              <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm space-y-1" data-testid="booking-estimate">
                <div className="flex justify-between"><span className="text-muted-foreground">Estimated total (incl. service & processing)</span>
                  <span className="font-bold">${estimate.total.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">50% deposit to lock (after vendor accepts)</span>
                  <span className="font-semibold">${estimate.deposit.toFixed(2)}</span></div>
                <p className="text-[11px] text-muted-foreground pt-1">No charge now. Final total is confirmed on the booking. Balance is due 7 days before the event.</p>
              </div>
            )}

            <Button className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={requesting} onClick={submit} data-testid="hire-confirm-btn">
              {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send booking request'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {CATS.map(c => (
                <button key={c.slug} onClick={() => setCat(c.slug)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${cat === c.slug
                    ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-border hover:border-fuchsia-500/60'}`}
                  data-testid={`hire-cat-${c.slug || 'all'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search suppliers…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="hire-search" />
            </div>
            {services === null ? (
              <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
            ) : list.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="hire-empty">No suppliers match — try another category.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {list.map(s => (
                  <div key={s.service_id} className="rounded-xl border border-border overflow-hidden hover:border-fuchsia-500/50 transition-colors"
                    data-testid={`hire-card-${s.service_id}`}>
                    <div className="h-28 bg-muted overflow-hidden">
                      {s.image_url && <img src={s.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold leading-snug line-clamp-1">{s.name}</p>
                        {s.host?.is_verified && <ShieldCheck className="h-4 w-4 text-emerald-400 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">{s.host?.business_name || 'Eventism supplier'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="outline" className="text-[10px] capitalize">{s.sub_category || s.category}</Badge>
                        <span className="text-xs font-semibold">${custPrice(s.base_price)}{PRICE_LABEL[s.pricing_model] || ''}</span>
                      </div>
                      <Button size="sm" className="w-full mt-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-8 text-xs"
                        onClick={() => pick(s)} data-testid={`hire-pick-${s.service_id}`}>
                        Request to book
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!showAll && filtered.length > 30 && (
              <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAll(true)} data-testid="hire-show-all">
                Show all {filtered.length} suppliers
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
